import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
} from "lib/headlessui/components/menu/menu";
import { EmailTemplateId } from "@sp-crm/core";
import { SearchInput } from "components/ui/search-input";
import { secondaryClasses } from "components/ui/secondary-button";
import React from "react";
import { EditorAdapter } from "roosterjs-editor-adapter";
import { ContentPosition } from "roosterjs-editor-types";
import { SignatureIcon, TriangleDownIcon } from "./ribbon-icons";
import { signatureClass, Template } from "./template";

interface Props {
    signatures?: Template[];
    editor: EditorAdapter;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(" ");
}

export const SignatureMenu: React.FC<Props> = ({ signatures, editor }) => {
    const handleSignature = React.useCallback(
        (templateId: EmailTemplateId) => {
            if (templateId && editor) {
                const signature =
                    (signatures || []).find(t => t.id === templateId) || null;
                if (signature) {
                    const doc = editor.getDocument();
                    const existingSignatures = Array.from(
                        doc.getElementsByClassName(signatureClass),
                    );
                    for (const n of existingSignatures) {
                        editor.deleteNode(n);
                    }
                    editor.insertContent(
                        `<div class="${signatureClass}">${signature.body}</div>`,
                        { position: ContentPosition.End },
                    );
                }
            }
        },
        [editor, signatures],
    );

    const [signatureFilter, setSignatureFilter] = React.useState("");

    const handleSignatureSearch = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSignatureFilter(e.target.value);
        },
        [],
    );

    const filteredSignatures = React.useMemo(() => {
        return signatures?.filter(t =>
            t.description.toLowerCase().includes(signatureFilter.toLowerCase()),
        );
    }, [signatures, signatureFilter]);

    if (signatures && signatures.length > 0) {
        return (
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton className={secondaryClasses}>
                    <div className="flex items-center space-x-2">
                        <SignatureIcon className="w-5 h-5" />
                        <TriangleDownIcon className="w-1.5 h-1.5" />
                    </div>
                </MenuButton>
                <MenuItems
                    anchor="bottom start"
                    className="w-56 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none [--anchor-gap:0.5rem]">
                    <p className="p-2 text-brand-700">Insert signature</p>
                    {signatures.length > 5 ? (
                        <SearchInput
                            value={signatureFilter}
                            placeholder="Filter signatures"
                            onChange={handleSignatureSearch}
                        />
                    ) : null}
                    {filteredSignatures.map(t => (
                        <MenuItem key={t.id}>
                            {({ focus }) => (
                                <div
                                    className={classNames(
                                        focus
                                            ? "bg-brand-100 text-brand-900"
                                            : "text-gray-700",
                                        "group flex items-center px-4 py-2 text-sm rounded-md",
                                    )}
                                    onClick={() => handleSignature(t.id)}>
                                    {t.description}
                                </div>
                            )}
                        </MenuItem>
                    ))}
                </MenuItems>
            </Menu>
        );
    }

    return null;
};
